import type { HTMLAttributes } from 'react';
import { cn } from '@/lib/cn';
import { flavorClass, type Flavor } from './flavors';

interface StickerProps extends HTMLAttributes<HTMLSpanElement> {
  flavor?: Flavor;
  /** Tilt in degrees. Default -3. */
  rotate?: number;
  /** Firm up the tilt on hover. Default true. */
  interactive?: boolean;
}

/**
 * A peel-and-stick label: chunky border, hard offset shadow, a flavor wash and
 * a jaunty tilt. Louder sibling of `Badge` — use for playful callouts, not
 * status. The whole kit's "graphical" voice lives here.
 *
 *   <Sticker flavor="citron">new</Sticker>
 *   <Sticker flavor="grape" rotate={4}>npm run new:feature</Sticker>
 */
export function Sticker({
  flavor = 'berry',
  rotate = -3,
  interactive = true,
  className,
  style,
  ...props
}: StickerProps) {
  const c = flavorClass[flavor];
  return (
    <span
      className={cn(
        'inline-flex select-none items-center gap-1.5 rounded-pill border-2 border-ink px-3 py-1',
        'font-display text-sm font-semibold text-ink shadow-hard',
        'transition-transform duration-200 ease-bounce',
        c.soft,
        interactive && 'hover:rotate-0 hover:scale-105',
        className,
      )}
      style={{ rotate: `${rotate}deg`, ...style }}
      {...props}
    />
  );
}
